import { Opaque } from 'lib/types';

export const BORDER_RADIUSES = {
  base: '1px',
  rounded: '4px',
  circle: '100px',
  pill: '24px',
};

export const COLORS = {
  indigo100: '#290087',
  indigo80: '#4F4099',
  indigo60: '#7E66B6',
  indigo50: '#8D89BB',
  indigo30: '#BDB8D9',
  indigo20: '#D4CCE7',
  indigo10: '#EAE6F3',
  yellow100: '#FFDC82',
  yellow10: '#FFF8E6',
  green100: '#62D284',
  green10: '#E0F6E6',
  red100: '#FF645A',
  red60: '#FF8F88',
  red10: '#FFE0DE',
  gray100: '#1E1B17',
  gray70: '#625F5D',
  gray50: '#9394A1',
  gray20: '#D2D1D1',
  gray8: '#EDEDEC',
  gray4: '#F6F6F7',
  black: '#000000',
  white: '#FFFFFF',
};

export const BOX_SHADOWS = {
  base: 'rgba(0, 0, 0, 0.4)',
  popover: `0px 14px 24px rgba(0, 0, 0, 0.12);`,
  card: '0px 2px 6px rgba(191, 191, 199, 0.4), 0px 2px 2px rgba(191, 191, 199, 0.24), 0px 0px 2px rgba(191, 191, 199, 0.28);',
};

/**
 * @deprecated use PDS font sizes from `theme` instead.
 */
export const FONT_SIZES = {
  hero: '44px',
  h1: '30px',
  h2: '24px',
  h3: '18px',
  h4: '16px',
  h5: '14px',
  h6: '14px',
  input: '16px',
  p: '16px',
  small: '14px',
  xs: '12px',
  code: '12px',
};

/**
 * @deprecated use PDS font weights from `theme` instead.
 *
 * TODO: These values don't look right after changing to Rubik. Should migrate
 * to weights defined in the design system everywhere.
 */
export const FONT_WEIGHTS = {
  light: '300',
  regular: '400',
  medium: '500',
  semiBold: '500',
  bold: '700',
};

/**
 * @deprecated use PDS line heights from `theme` instead.
 */
export const LINE_HEIGHTS = {
  hero: 1.27,
  h1: 1.33,
  h2: 1.45,
  h3: 1.44,
  h4: 1.5,
  h5: 1.71,
  h6: 1.71,
  input: 1.5,
  p: 1.5,
  a: 1.5,
  small: 1.5,
  xs: 1.75,
  code: 1.5,
};

/**
 * Opaque type to enforce consistency of breakpoints.
 * If REALLY needed, can be imported and used to type a one-off breakpoint.
 */
export type Breakpoint = Opaque<number, 'Breakpoint'>;

/**
 * Screen height breakpoints.
 *
 * Each number represents the MAX size for the given breakpoint.
 */
export const MEDIA_HEIGHTS: Record<string, Breakpoint> = {
  // iPhone 5 height
  mobile: 568 as Breakpoint,
};

/**
 * Screen width breakpoints. Values are fairly standard and are stolen from Bulma.
 * https://bulma.io/documentation/modifiers/responsive-helpers/
 *
 * Each number represents the MAX size for the given breakpoint.
 */
export const MEDIA_WIDTHS: Record<string, Breakpoint> = {
  // Based on Bootstrap's definition of small devices
  mobileSmall: 576 as Breakpoint,
  mobile: 768.98 as Breakpoint,
  tablet: 769 as Breakpoint,
  desktop: 1024 as Breakpoint,
  widescreen: 1216 as Breakpoint,
  fullHD: 1408 as Breakpoint,
};

// Add 0.02 to width so it doesn't overlap with mobile width, this is bootstrap's approach
const PIXEL_FRACTION = 0.02;

// This doesn't match page widths because we want
// some pages to be smaller than mobile sizes on desktop.
export const MEDIA_QUERIES = {
  isSmallVertical: `(max-height: ${MEDIA_HEIGHTS.mobile - PIXEL_FRACTION}px)`,
  isMobileSmall: `(max-width: ${MEDIA_WIDTHS.mobileSmall - PIXEL_FRACTION}px), (max-height: ${
    MEDIA_HEIGHTS.mobile - PIXEL_FRACTION
  }px)`,
  isMobile: `(max-width: ${MEDIA_WIDTHS.tablet - PIXEL_FRACTION}px), (max-height: ${
    MEDIA_HEIGHTS.mobile - PIXEL_FRACTION
  }px)`,
  isNotMobile: `(min-width: ${MEDIA_WIDTHS.tablet}px) and (min-height: ${MEDIA_HEIGHTS.mobile}px)`,
  isTablet: `(min-width: ${MEDIA_WIDTHS.tablet}px) and (max-width: ${
    MEDIA_WIDTHS.desktop - PIXEL_FRACTION
  }px)`,
  isDesktop: `(min-width: ${MEDIA_WIDTHS.desktop}px) and (max-width: ${
    MEDIA_WIDTHS.widescreen - PIXEL_FRACTION
  }px)`,
  isWidescreen: `(min-width: ${MEDIA_WIDTHS.widescreen}px) and (max-width: ${
    MEDIA_WIDTHS.fullHD - PIXEL_FRACTION
  }px)`,
  isFullHD: `(min-width: ${MEDIA_WIDTHS.fullHD}px)`,
};

export function mediaQuery({ min, max }: { min?: Breakpoint; max?: Breakpoint }): string {
  if (min == null && max == null) {
    throw new Error('Must provide either min or max');
  } else if (min != null && max == null) {
    return `(min-width: ${min}px)`;
  } else if (min == null && max != null) {
    return `(max-width: ${max - PIXEL_FRACTION}px)`;
  } else {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return `(min-width: ${min}px) and (max-width: ${max - PIXEL_FRACTION}px)`;
  }
}

export const SPACINGS = {
  /**
   * @deprecated use `import { BASE } from 'design-system/variables/spacing';`
   */
  base: 8,
  widgetPadding: '24px',
  inputPadding: '16px',
  navHeight: '48px',
};

export const HEIGHTS = {
  input: '48px',
  textArea: '96px',
};

export const TRANSITIONS = {
  fast: '0.2s',
  medium: '0.4s',
};
